import { yupResolver } from '@hookform/resolvers/yup';
import { useSendSystemMessage } from 'all-in-one-shared-api';
import { message } from 'antd';
import { PrimaryButton, SecondaryButton } from 'components';
import { FormDateRangePicker, FormInput, FormWrapper, FormRichTextEditor } from 'components/form';
import { StyledTitle } from 'components/StyledTitle';
import { useKeycloakReady } from 'hooks';
import { MessageType } from 'models/MessageType';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { breakpoint } from 'styled-components-breakpoint';
import { array, date, object, string } from 'yup';
import { AdminUtils, SystemAnnouncementFormState } from './utils';
const Wrapper = styled.div`
  padding: 1rem;

  ${breakpoint('desktop')`
    width: 50%;
  `}
`;

const FormItemWrapper = styled.div`
  display: grid;
  row-gap: 24px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const getSystemAnnouncementSchema = (t: TFunction) =>
  object({
    title: string().optional(),
    dateRange: array(date()).required(t('common.error.requiredField')).nullable(),
    message: string().required(t('common.error.requiredField')),
  });

const SystemAnnouncementPage = () => {
  const { t } = useTranslation();
  const { token } = useKeycloakReady();
  const [isEdit, setIsEdit] = useState(false);

  const methods = useForm<SystemAnnouncementFormState>({
    resolver: yupResolver(getSystemAnnouncementSchema(t)),
  });

  useEffect(() => {
    if (isEdit) return;

    methods.clearErrors();
  }, [isEdit, methods]);

  const { shouldFetch } = useSendSystemMessage(',', token || '', AdminUtils.mapSendSystemMessage(methods.getValues()), {
    onSuccess: () => {
      message.success('Updated');
      shouldFetch(false);
      setIsEdit(false);
    },
    onError: () => {
      message[MessageType.ERROR](t('common.error.generic'));
      shouldFetch(false);
    },
  });

  const onSubmit = () => {
    shouldFetch(true);
  };
  return (
    <Wrapper>
      <StyledTitle level={2}>{t('admin.systemAnnouncementMessage.title')}</StyledTitle>
      <div style={{ paddingTop: '20px' }}></div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <FormItemWrapper>
            <FormWrapper name={'title'} label={t('admin.systemAnnouncementMessage.messageTitle')}>
              <FormInput
                disabled={!isEdit}
                name={'title'}
                placeholder={t('admin.systemAnnouncementMessage.messageTitlePlaceholder')}
              />
            </FormWrapper>

            <FormWrapper name={'dateRange'} label={t('admin.systemAnnouncementMessage.dateRangeTitle')}>
              <FormDateRangePicker disabled={!isEdit} name={'dateRange'} />
            </FormWrapper>

            <FormWrapper name={'message'} label={t('admin.systemAnnouncementMessage.messageContentTitle')}>
              <FormRichTextEditor
                disabled={!isEdit}
                name={'message'}
                placeholder={t('admin.systemAnnouncementMessage.messageContentPlaceholder')}
              />
            </FormWrapper>

            {isEdit ? (
              <ButtonWrapper>
                <PrimaryButton htmlType="submit">{t('common.save')}</PrimaryButton>
                <SecondaryButton htmlType="button" onClick={() => setIsEdit(false)}>
                  {t('common.cancel')}
                </SecondaryButton>
              </ButtonWrapper>
            ) : (
              <div>
                <PrimaryButton htmlType="button" onClick={() => setIsEdit(true)}>
                  {t('common.edit')}
                </PrimaryButton>
              </div>
            )}
          </FormItemWrapper>
        </form>
      </FormProvider>
    </Wrapper>
  );
};

export default SystemAnnouncementPage;
